import { Link, NavLink, useParams } from "react-router-dom";
import { useCustomer } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { viewAsUrl } from "../viewAs";
import { ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, vtag, btnSecondary } from "../components/ui";
import { AlarmsTab } from "./customer/AlarmsTab";
import { AccessTab } from "./customer/AccessTab";
import { AuditTab } from "./customer/AuditTab";
import { ConcurrencyTab } from "./customer/ConcurrencyTab";
import { FlowConfigTab } from "./customer/FlowConfigTab";
import { JourneysTab } from "./customer/JourneysTab";
import { OnboardingPanel } from "./customer/OnboardingPanel";
import { QuotasTab } from "./customer/QuotasTab";
import { SupportCasesTab } from "./customer/SupportCasesTab";
import { TrustedAdvisorTab } from "./customer/TrustedAdvisorTab";

const TABS = [
  { id: "alarms", label: "Alarms" },
  { id: "quotas", label: "Service Quotas" },
  { id: "trusted-advisor", label: "Trusted Advisor" },
  { id: "support", label: "AWS Support Cases" },
  { id: "concurrency", label: "Concurrency" },
  { id: "journeys", label: "Journeys" },
  { id: "flow-config", label: "Flow configuration" },
  { id: "access", label: "Access" },
  { id: "audit", label: "Audit" },
] as const;
type TabId = (typeof TABS)[number]["id"];

/** design.md "Account drill-down": one customer, everything the system knows, in tabs. */
export function CustomerDetailPage() {
  const { environmentId = "", tab = "alarms" } = useParams();
  const { me } = useAuth();
  const q = useCustomer(environmentId);
  if (q.isPending) return <LoadingState label="Loading customer" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { customer: c, currentTemplateVersion, enabledComponents } = q.data;
  const active = (TABS.find((t) => t.id === tab)?.id ?? "alarms") as TabId;
  const heartbeatStale = !c.last_heartbeat_at || Date.now() - Date.parse(c.last_heartbeat_at) > 3 * 3_600_000;

  return (
    <>
      <PageHeader
        title={c.friendly_name}
        subtitle={`AWS Account ${c.aws_account_number}`}
        actions={
          <div className="flex flex-wrap items-start gap-6">
          {me?.role === "internal-team" && !me.viewAs && (
            // ADR-0011: the customer's own portal, in a new tab, read-only until asked otherwise. rel="opener" on purpose:
            // the session lives in sessionStorage, and only a tab with an opener inherits a copy (same origin, so nothing leaks).
            <a href={viewAsUrl(c.organisation_id, `/customers/${environmentId}`)} target="_blank" rel="opener" className={btnSecondary}>
              View their portal
            </a>
          )}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            <dt className="text-neutral-500">Status</dt>
            <dd><StatusPill status={c.status} tone={c.status === "active" ? "ok" : "neutral"} /></dd>
            <dt className="text-neutral-500" title="When a test event last came through the same path a real alarm takes. The stack sends one on every create or update; the heartbeat is a separate, hourly signal.">
              Alarm path tested
            </dt>
            <dd>{c.connectivity_verified_at ? <RelativeTime iso={c.connectivity_verified_at} /> : <StatusPill status="never" tone="warning" />}</dd>
            <dt className="text-neutral-500">Heartbeat</dt>
            <dd><StatusPill status={heartbeatStale ? "no heartbeat" : "reporting"} tone={heartbeatStale ? "warning" : "ok"} /> <RelativeTime iso={c.last_heartbeat_at} /></dd>
            <dt className="text-neutral-500">Template</dt>
            <dd>
              {c.template_version ? (
                <Link to={`/release-notes#template-${vtag(c.template_version)}`} className="text-accent-strong underline dark:text-accent">
                  {vtag(c.template_version)}
                </Link>
              ) : (
                vtag(c.template_version)
              )}
              {c.template_version && c.template_version !== currentTemplateVersion && (
                <span className="ml-1 text-amber-700 dark:text-amber-300">
                  (current <Link to={`/release-notes#template-${vtag(currentTemplateVersion)}`} className="underline">{vtag(currentTemplateVersion)}</Link>)
                </span>
              )}
            </dd>
          </dl>
          </div>
        }
      />
      {me?.role === "customer" && (c.onboarding_status !== "active" || !c.last_heartbeat_at || c.template_version !== currentTemplateVersion) && <OnboardingPanel detail={q.data} />}
      {(c.forwarder_dlq_depth ?? 0) > 0 && (
        <p role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          {c.forwarder_dlq_depth} alarm notification{c.forwarder_dlq_depth === 1 ? "" : "s"} could not be delivered from this account and sit in its dead-letter queue.
        </p>
      )}
      <nav aria-label="Customer sections" className="mb-4 border-b border-neutral-200 dark:border-neutral-800">
        <ul className="flex gap-1">
          {TABS.map((t) => (
            <li key={t.id}>
              <NavLink to={`/customers/${environmentId}/${t.id}`} aria-current={active === t.id ? "page" : undefined} className={`inline-block border-b-2 px-3 py-3 text-sm font-medium transition-colors ${active === t.id ? "border-accent text-accent-strong dark:text-white" : "border-transparent text-neutral-600 hover:border-neutral-300 hover:text-ink dark:text-neutral-400 dark:hover:text-white"}`}>
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      {active === "alarms" && <AlarmsTab environmentId={environmentId} />}
      {active === "quotas" && <QuotasTab environmentId={environmentId} canRequestIncrease={me?.role === "internal-team" && enabledComponents.includes("QuotaIncreaseRequestRole")} />}
      {active === "trusted-advisor" && <TrustedAdvisorTab environmentId={environmentId} available={q.data.trustedAdvisorAvailable} />}
      {active === "support" && <SupportCasesTab environmentId={environmentId} />}
      {active === "concurrency" && <ConcurrencyTab environmentId={environmentId} />}
      {active === "audit" && <AuditTab environmentId={environmentId} />}
      {active === "access" && <AccessTab environmentId={environmentId} />}
      {active === "flow-config" && <FlowConfigTab environmentId={environmentId} enabled={enabledComponents.includes("FlowConfiguration")} />}
      {active === "journeys" && <JourneysTab environmentId={environmentId} canInspect={me?.role === "internal-team" && enabledComponents.includes("JourneyInspectorRole")} aggregationEnabled={Boolean(c.journey_config?.contact_flow_log_group) || enabledComponents.includes("JourneyInspectorRole")} />}
    </>
  );
}
